// Ported from WEEKLY_2.HTM's PlanDefaultsTab.
import { useState } from "react";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { DAYS, MEAL_SLOTS, COLORS, FONT_SIZE, RADIUS, SPACE } from "../lib/constants";
import { orderedDays, toISODate, formatMinutes } from "../lib/utils";
import { defaultPlanDefaults, dayBudgetFromDefaults, emptyHouseholdMember, mergeImportedMembers } from "../lib/planUtils";
import { HouseholdPanel } from "../components/HouseholdPanel";
import { AccentPicker } from "../components/AccentPicker";

export function PlanDefaultsPage() {
  const { userId, planDefaults, setPlanDefaults, recipes, setRecipes, plansByWeek, setPlansByWeek, colors } = useMealPlanner();
  const { card, ink, soft, accent, danger, line } = colors;
  const [importError, setImportError] = useState("");
  const [confirmImport, setConfirmImport] = useState(null);
  // id of the unlinked person the viewer is about to claim -- claiming is a
  // two-step click because it locks everyone else out of that row's numbers.
  const [claimingId, setClaimingId] = useState(null);

  const people = planDefaults.householdMembers || [];
  const hasMine = people.some((m) => m.userId && m.userId === userId);
  const unlinkedPeople = people.filter((m) => !m.userId);

  function updatePerson(id, patch) {
    setPlanDefaults((pd) => ({ ...pd, householdMembers: pd.householdMembers.map((m) => (m.id === id ? { ...m, ...patch } : m)) }));
  }

  function setMealBudget(day, slot, value) {
    setPlanDefaults((pd) => {
      const mealTimeBudgets = { ...(pd.mealTimeBudgets || {}) };
      const dayMeals = { ...(mealTimeBudgets[day] || {}) };
      if (value === "") delete dayMeals[slot];
      else dayMeals[slot] = Math.max(0, Number(value) || 0);
      mealTimeBudgets[day] = dayMeals;
      return { ...pd, mealTimeBudgets };
    });
  }

  function handleExport() {
    const payload = {
      exportedAt: new Date().toISOString(),
      recipes,
      plansByWeek,
      planDefaults,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `weekly-kitchen-backup-${toISODate(new Date())}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleFileSelected(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportError("");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (!data || typeof data !== "object" || !Array.isArray(data.recipes)) {
          setImportError("That file doesn't look like a weekly kitchen backup.");
          return;
        }
        setConfirmImport(data);
      } catch {
        setImportError("Couldn't read that file — make sure it's a backup exported from this app.");
      }
    };
    reader.readAsText(file);
  }

  function applyImport() {
    if (!confirmImport) return;
    setRecipes(confirmImport.recipes || []);
    setPlansByWeek(confirmImport.plansByWeek || {});
    const imported = confirmImport.planDefaults || {};
    setPlanDefaults((current) => ({
      ...defaultPlanDefaults(),
      ...imported,
      // A restore must not rewire who owns whose numbers -- see mergeImportedMembers.
      householdMembers: mergeImportedMembers(current.householdMembers, imported.householdMembers),
    }));
    setConfirmImport(null);
  }

  const dayTotal = (day) => dayBudgetFromDefaults(planDefaults, day);
  const weekTotal = DAYS.reduce((sum, day) => sum + dayTotal(day), 0);

  const cellInputStyle = { width: 52, padding: "var(--space-xs) var(--space-sm)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, fontSize: FONT_SIZE.sm, color: ink, textAlign: "center" };

  return (
    <div className="mp-sans">
      <p style={{ fontSize: FONT_SIZE.base, color: soft, marginBottom: SPACE.x3, maxWidth: 640 }}>
        These defaults apply automatically to any new week you haven't customized yet. Once you edit a specific week's servings, time budgets, or plan, that week keeps its own values from then on.
      </p>

      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xxl) var(--space-xxl)", marginBottom: SPACE.x3, maxWidth: 320 }}>
        <label style={{ fontSize: FONT_SIZE.base, fontWeight: 700, display: "block", marginBottom: SPACE.md }}>People served</label>
        <div style={{ display: "flex", alignItems: "center", gap: SPACE.md }}>
          <input
            type="number"
            min="1"
            value={planDefaults.defaultServings}
            onChange={(e) => setPlanDefaults((pd) => ({ ...pd, defaultServings: Math.max(1, Number(e.target.value) || 1) }))}
            style={{ width: 70, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, fontSize: FONT_SIZE.md, color: ink, fontWeight: 700 }}
          />
          <span style={{ fontSize: FONT_SIZE.base, color: soft }}>the default for new weekly plans</span>
        </div>
      </div>

      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xxl) var(--space-xxl)", marginBottom: SPACE.x3, maxWidth: 320 }}>
        <label style={{ fontSize: FONT_SIZE.base, fontWeight: 700, display: "block", marginBottom: SPACE.md }}>Week starts on</label>
        <select
          value={planDefaults.weekStartDay || "Monday"}
          onChange={(e) => setPlanDefaults((pd) => ({ ...pd, weekStartDay: e.target.value }))}
          style={{ padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, fontSize: FONT_SIZE.md, color: ink, fontWeight: 700 }}
        >
          {DAYS.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <p style={{ fontSize: FONT_SIZE.sm, color: soft, margin: "var(--space-lg) 0 0" }}>
          This is a global setting, not a per-week override — it controls the day order everywhere in the app (the weekly planner, time budgets, "Add to plan" day pickers) and which date "this week" navigates to.
        </p>
      </div>

      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xxl) var(--space-xxl)", marginBottom: SPACE.x3 }}>
        <label style={{ fontSize: FONT_SIZE.base, fontWeight: 700, display: "block", marginBottom: SPACE.xs }}>Time budget (minutes)</label>
        <p style={{ fontSize: FONT_SIZE.sm, color: soft, margin: "0 0 var(--space-xl)" }}>
          Set a default per meal; each day's total is the sum of its three meals, and that becomes the day's starting time budget on a new week.
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: FONT_SIZE.sm, minWidth: 480 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "var(--space-xs) var(--space-md)", color: soft, fontWeight: 500 }}></th>
                {MEAL_SLOTS.map((slot) => (
                  <th key={slot} style={{ textAlign: "center", padding: "var(--space-xs) var(--space-md)", color: soft, fontWeight: 500, textTransform: "capitalize" }}>
                    {slot}
                  </th>
                ))}
                <th style={{ textAlign: "center", padding: "var(--space-xs) var(--space-md)", color: accent, fontWeight: 700 }}>Day total</th>
              </tr>
            </thead>
            <tbody>
              {orderedDays(planDefaults.weekStartDay).map((day) => (
                <tr key={day}>
                  <td style={{ padding: "var(--space-xs) var(--space-md)", fontWeight: 600, whiteSpace: "nowrap" }}>{day}</td>
                  {MEAL_SLOTS.map((slot) => (
                    <td key={slot} style={{ padding: "var(--space-xs) var(--space-md)", textAlign: "center" }}>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={planDefaults.mealTimeBudgets?.[day]?.[slot] ?? ""}
                        onChange={(e) => setMealBudget(day, slot, e.target.value)}
                        style={cellInputStyle}
                      />
                    </td>
                  ))}
                  <td style={{ padding: "var(--space-xs) var(--space-md)", textAlign: "center", fontWeight: 700, color: accent }}>{formatMinutes(dayTotal(day))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: SPACE.xl, fontSize: FONT_SIZE.base }}>
          <span style={{ color: soft }}>Weekly total from these defaults: </span>
          <span style={{ fontWeight: 700, color: accent }}>{formatMinutes(weekTotal)}</span>
        </div>
      </div>

      <div id="people-section" style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xxl) var(--space-xxl)" }}>
        <label style={{ fontSize: FONT_SIZE.base, fontWeight: 700, display: "block", marginBottom: SPACE.xs }}>People & daily nutrition targets</label>
        <p style={{ fontSize: FONT_SIZE.sm, color: soft, margin: "0 0 var(--space-xl2)" }}>
          Add a row per person so each can have their own targets. Leave any field at 0 to leave it untracked for that person. Add nutrition info per serving on individual recipes for this to add up.
        </p>

        {!hasMine && unlinkedPeople.length > 0 && (
          <div style={{ background: COLORS.card, border: `1px solid ${accent}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl)", marginBottom: SPACE.xl }}>
            <p style={{ fontSize: FONT_SIZE.base, color: ink, margin: "0 0 var(--space-xs)", fontWeight: 700 }}>Which one is you?</p>
            <p style={{ fontSize: FONT_SIZE.sm, color: soft, margin: 0 }}>
              Claim your row so your targets stay private. Once it's yours, only you can see or change its numbers — not anyone else in this household. Rows nobody has claimed (like a child's) stay open to everyone here.
            </p>
          </div>
        )}

        {people.length === 0 && <p style={{ fontSize: FONT_SIZE.base, color: soft, margin: "0 0 var(--space-xl)" }}>No people added yet.</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.lg, marginBottom: SPACE.xl }}>
          {people.map((member) => {
            const isMine = !!member.userId && member.userId === userId;

            // Linked to someone else: the name is visible, the numbers are
            // theirs alone -- and so is the row (the server refuses edits
            // and deletes from anyone else).
            if (member.targetsHidden) {
              return (
                <div key={member.id} style={{ background: COLORS.card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl)" }}>
                  <span style={{ fontSize: FONT_SIZE.base, fontWeight: 700, color: ink }}>{member.name || "Someone"}</span>
                  <span style={{ fontSize: FONT_SIZE.sm, color: soft, marginLeft: SPACE.lg }}>Their targets are private to them.</span>
                </div>
              );
            }

            return (
              <div key={member.id} style={{ background: COLORS.card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl)" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.9fr 0.9fr 0.9fr 0.9fr auto", gap: SPACE.lg, alignItems: "end" }}>
                  <div>
                    <label style={{ fontSize: FONT_SIZE.xs, color: soft, display: "block", marginBottom: SPACE.xs }}>Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Alex"
                      value={member.name}
                      onChange={(e) => updatePerson(member.id, { name: e.target.value })}
                      style={{ width: "100%", padding: "var(--space-sm) var(--space-md)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, fontSize: FONT_SIZE.base, color: ink, boxSizing: "border-box" }}
                    />
                  </div>
                  {[
                    ["dailyCalorieTarget", "Calories"],
                    ["dailyProteinTarget", "Protein (g)"],
                    ["dailyCarbsTarget", "Carbs (g)"],
                    ["dailyFatTarget", "Fat (g)"],
                  ].map(([field, label]) => (
                    <div key={field}>
                      <label style={{ fontSize: FONT_SIZE.xs, color: soft, display: "block", marginBottom: SPACE.xs }}>{label}</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={member[field] || ""}
                        onChange={(e) => updatePerson(member.id, { [field]: Math.max(0, Number(e.target.value) || 0) })}
                        style={{ width: "100%", padding: "var(--space-sm) var(--space-md)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, fontSize: FONT_SIZE.base, color: ink, fontWeight: 700, boxSizing: "border-box" }}
                      />
                    </div>
                  ))}
                  <button
                    className="mp-btn"
                    onClick={() => setPlanDefaults((pd) => ({ ...pd, householdMembers: pd.householdMembers.filter((m) => m.id !== member.id) }))}
                    style={{ background: "transparent", color: colors.danger, fontSize: FONT_SIZE.xl, padding: "var(--space-sm) var(--space-xs)" }}
                    aria-label="Remove person"
                  >
                    ×
                  </button>
                </div>

                {isMine && (
                  <p style={{ fontSize: FONT_SIZE.sm, color: accent, margin: "var(--space-md) 0 0" }}>
                    <span style={{ fontWeight: 700 }}>This is you.</span> Only you can see or change these numbers.
                  </p>
                )}
                {!member.userId && !hasMine && claimingId !== member.id && (
                  <button
                    className="mp-btn mp-sans"
                    onClick={() => setClaimingId(member.id)}
                    style={{ background: "transparent", border: `1px solid ${line}`, color: accent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm, marginTop: SPACE.md }}
                  >
                    This is me
                  </button>
                )}
                {!member.userId && claimingId === member.id && (
                  <div style={{ marginTop: SPACE.md, display: "flex", gap: SPACE.lg, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: FONT_SIZE.sm, color: ink }}>Only you will be able to see or change this row's numbers.</span>
                    <button
                      className="mp-btn"
                      onClick={() => {
                        updatePerson(member.id, { userId });
                        setClaimingId(null);
                      }}
                      style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}
                    >
                      Claim it
                    </button>
                    <button
                      className="mp-btn"
                      onClick={() => setClaimingId(null)}
                      style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: SPACE.lg, flexWrap: "wrap" }}>
          {!hasMine && (
            <button
              className="mp-btn mp-sans"
              onClick={() => setPlanDefaults((pd) => ({ ...pd, householdMembers: [...(pd.householdMembers || []), emptyHouseholdMember(userId)] }))}
              style={{ background: "transparent", border: `1px solid ${line}`, color: accent, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}
            >
              + Add me
            </button>
          )}
          <button
            className="mp-btn mp-sans"
            onClick={() => setPlanDefaults((pd) => ({ ...pd, householdMembers: [...(pd.householdMembers || []), emptyHouseholdMember()] }))}
            style={{ background: "transparent", border: `1px solid ${line}`, color: accent, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}
          >
            {hasMine ? "+ Add a person" : "+ Add someone else"}
          </button>
        </div>
      </div>

      <HouseholdPanel />

      <AccentPicker />

      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xxl) var(--space-xxl)", marginTop: SPACE.x3, maxWidth: 560 }}>
        <label style={{ fontSize: FONT_SIZE.base, fontWeight: 700, display: "block", marginBottom: SPACE.xs }}>Backup & transfer</label>
        <p style={{ fontSize: FONT_SIZE.sm, color: soft, margin: "0 0 var(--space-xl2)" }}>
          Everything here — recipes, weekly plans, and these defaults — lives in this app's storage (currently your browser's local storage; see the README for the plan to move this to Supabase). Download a backup any time as insurance, or to move your data somewhere else later.
        </p>
        <div style={{ display: "flex", gap: SPACE.lg, flexWrap: "wrap", alignItems: "center" }}>
          <button className="mp-btn mp-sans" onClick={handleExport} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-md) var(--space-xxl)", borderRadius: RADIUS.lg, fontSize: FONT_SIZE.base, fontWeight: 500 }}>
            Download backup (.json)
          </button>
          <label className="mp-btn mp-sans" style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-md) var(--space-xxl)", borderRadius: RADIUS.lg, fontSize: FONT_SIZE.base, cursor: "pointer" }}>
            Restore from backup
            <input type="file" accept="application/json" onChange={handleFileSelected} style={{ display: "none" }} />
          </label>
        </div>
        {importError && (
          <p style={{ fontSize: FONT_SIZE.sm, color: danger, margin: "var(--space-lg) 0 0" }}>{importError}</p>
        )}
        {confirmImport && (
          <div className="mp-sans" style={{ marginTop: SPACE.xl2, background: COLORS.card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-xl) var(--space-xl2)" }}>
            <p style={{ fontSize: FONT_SIZE.base, color: ink, margin: "0 0 var(--space-lg)", fontWeight: 700 }}>
              This will replace everything currently in the app — {recipes.length} recipe{recipes.length === 1 ? "" : "s"} and {Object.keys(plansByWeek).length} saved week{Object.keys(plansByWeek).length === 1 ? "" : "s"} — with the contents of this backup ({confirmImport.recipes?.length || 0} recipes, {Object.keys(confirmImport.plansByWeek || {}).length} weeks). This can't be undone.
            </p>
            <div style={{ display: "flex", gap: SPACE.lg }}>
              <button className="mp-btn" onClick={applyImport} style={{ background: danger, color: COLORS.onAccent, padding: "var(--space-sm) var(--space-xl2)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}>
                Replace with backup
              </button>
              <button className="mp-btn" onClick={() => setConfirmImport(null)} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-sm) var(--space-xl2)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
